import { useCallback, useRef } from 'react';
import { useAtom } from 'jotai';
import {
    signatureModeAtom,
    signatureFontFamilyAtom,
} from '~/modules/ecommerce/states/accept-quote-popup-atoms';

interface UseSignatureCanvasProps {
    /** The name to display as typed signature */
    name: string;
}

interface UseSignatureCanvasReturn {
    /** Whether to use drawn signature mode */
    useDrawn: boolean;
    /** Current font family for typed signature */
    fontFamily: string;
    /** Function to set the font family */
    setFontFamily: (fontFamily: string) => void;
    /** Function to toggle signature mode */
    toggleSignatureMode: () => void;
    /** Function to clear the canvas */
    clearCanvas: () => void;
    /** Function to set the clear canvas ref */
    setClearCanvasRef: (clearFn: () => void) => void;
}

/**
 * Custom hook to manage signature canvas UI state
 * Handles signature mode switching (drawn vs typed) and font selection
 * Note: Signature data, mode, and font family are all managed by atoms for global state sharing
 */
export const useSignatureCanvas = ({
    name: _name,
}: UseSignatureCanvasProps): UseSignatureCanvasReturn => {
    const [useDrawn, setUseDrawn] = useAtom(signatureModeAtom);
    const [fontFamily, setFontFamily] = useAtom(signatureFontFamilyAtom);

    // Ref to store clear canvas function from the canvas component
    const clearCanvasRef = useRef<(() => void) | null>(null);

    // Toggle between drawn and typed signature modes
    const toggleSignatureMode = useCallback(() => {
        const newUseDrawn = !useDrawn;
        setUseDrawn(newUseDrawn);

        // Clear canvas when switching to drawn mode to remove any typed signature
        if (newUseDrawn && clearCanvasRef.current) {
            clearCanvasRef.current();
        }
    }, [useDrawn, setUseDrawn]);

    // Clear canvas function
    const clearCanvas = useCallback(() => {
        if (clearCanvasRef.current) {
            clearCanvasRef.current();
        }
    }, []);

    // Function to set the clear canvas ref (called by the canvas component)
    const setClearCanvasRef = useCallback((clearFn: () => void) => {
        clearCanvasRef.current = clearFn;
    }, []);

    return {
        useDrawn,
        fontFamily,
        setFontFamily,
        toggleSignatureMode,
        clearCanvas,
        setClearCanvasRef,
    };
};
